<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <el-select class="input-label col-span-12 md:col-span-2" v-model="selectedValue">
        <el-option key="quickView" :label="$t('views.common.listView.quickView')" value="quickView">
        </el-option>
        <el-option key="allItems" :label="$t('views.common.listView.allItems')" value="allItems">
        </el-option>
        <el-option
          key="export"
          :label="$t('views.common.listView.selectedRows.export')"
          value="export"
        >
        </el-option>

        <el-option
          key="delete"
          :label="$t('views.common.listView.selectedRows.delete')"
          value="delete"
        >
        </el-option>
      </el-select>

      <el-input
        :placeholder="$t('views.common.listView.searchRows')"
        class="w-full col-span-12 md:col-span-6"
        :prefix-icon="Search"
        v-model="searchTerm"
      />

      <div class="w-full flex items-center gap-2 col-span-12 md:col-span-4 justify-stretch">
        <div class="flex gap-2 grow">
          <horizontal-button
            :title="t('newPet')"
            @click.prevent="$router.push($Route.PETS_NEW_PET)"
            v-if="$can('POST', '/customer/pets')"
          />
        </div>
      </div>
    </div>

    <ag-grid-vue
      v-if="rowModelType"
      class="ag-theme-alpine mt-4 md:mt-0"
      :columnDefs="columnDefs"
      rowSelection="multiple"
      :enable-rtl="$ctx.getDir() === 'rtl'"
      style="height: 85vh"
      @cell-double-clicked="onCellDoubleClicked"
      :defaultColDef="defaultColDef"
      :rowData="rowData"
      :pagination="selectedValue != 'quickView'"
      :paginationAutoPageSize="selectedValue != 'quickView'"
      :onGridReady="onGridReady"
      :rowBuffer="rowBuffer"
      :rowModelType="rowModelType"
      :cacheBlockSize="cacheBlockSize"
      :cacheOverflowSize="cacheOverflowSize"
      :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
      :infiniteInitialRowCount="infiniteInitialRowCount"
      :maxBlocksInCache="maxBlocksInCache"
    >
    </ag-grid-vue>

    <div class="mt-2">{{ $t('views.common.listView.count') }}: {{ petCount }}</div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'

// components
import { AgGridVue } from 'ag-grid-vue3'
import {
  ColDef,
  GridApi,
  GridOptions,
  IGetRowsParams,
  InfiniteRowModelModule,
  ModuleRegistry
} from 'ag-grid-community'
import HorizontalButton from '@/components/HorizontalButton.vue'

import Pet, { PetsPaginated, petI18nMessages } from '@/models/Pet'
import { useI18n } from 'vue-i18n'
import { Search } from '@element-plus/icons-vue'
import { onBeforeMount } from 'vue'
import { formatPostgresDate } from '@/utils/date'
import { useStorage } from '@vueuse/core'

ModuleRegistry.registerModules([InfiniteRowModelModule])

export default defineComponent({
  name: 'pets',

  setup() {
    const { t } = useI18n({
      messages: petI18nMessages
    })

    return { t, Search }
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: Pet[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}

    const rowBuffer = ref<any>(null)
    const rowSelection = ref<any>(null)
    const rowModelType = ref<any>(null)
    const cacheBlockSize = ref<any>(null)
    const cacheOverflowSize = ref<any>(null)
    const maxConcurrentDatasourceRequests = ref<any>(null)
    const infiniteInitialRowCount = ref<any>(null)
    const maxBlocksInCache = ref<any>(null)

    onBeforeMount(() => {
      rowBuffer.value = 0
      rowSelection.value = 'multiple'
      rowModelType.value = 'infinite'
      cacheBlockSize.value = 100
      cacheOverflowSize.value = 100
      maxConcurrentDatasourceRequests.value = 2
      infiniteInitialRowCount.value = 0
      maxBlocksInCache.value = 10
    })

    return {
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,

      searchTerm: useStorage('pets-search-term', ''),
      totalResultCount: useStorage('pets-total-result-count', 0),
      selectedValue: 'quickView',
      gridApi: undefined as GridApi | undefined,
      rowBuffer,
      rowSelection,
      rowModelType,
      cacheBlockSize,
      cacheOverflowSize,
      maxConcurrentDatasourceRequests,
      infiniteInitialRowCount,
      maxBlocksInCache,
      petCount: 0
    }
  },

  components: {
    AgGridVue,
    HorizontalButton
  },

  async beforeMount() {
    try {
      this.petCount = (
        await this.$http.get<{ count: number }>('/helpers/count?model=customer_pets')
      ).count
    } catch (error: any) {
      this.$alertModal.showDanger({ title: error.title, body: error.body })
    }

    this.defaultColDef = {
      sortable: false,
      filter: false,
      resizable: true,
      minWidth: 150
    }

    this.columnDefs = [
      {
        field: '-',
        hide: true,
        lockVisible: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      },
      {
        headerName: this.t('petName'),
        field: 'name',
        checkboxSelection: true,
        headerCheckboxSelection: true
      },
      {
        headerName: this.t('type'),
        field: 'type'
      },
      {
        headerName: this.t('breed'),
        field: 'breed'
      },
      {
        headerName: this.t('color'),
        field: 'color'
      },
      {
        headerName: this.t('sex'),
        field: 'sex',
        valueGetter: (params) => {
          const pet = params.node?.data as Pet
          if (!pet) return ''
          return pet.sex === 'male' ? this.t('male') : pet.sex === 'female' ? this.t('female') : ''
        }
      },
      {
        headerName: this.t('dateOfBirth'),
        field: 'dateOfBirth',
        valueGetter: (params) => {
          const pet = params.node?.data as Pet
          if (!pet || !pet.dateOfBirth) return ''
          return formatPostgresDate(pet.dateOfBirth.toString())
        }
      },
      {
        headerName: this.t('ownerName'),
        valueGetter: (params) => {
          const pet = params.node?.data as Pet
          if (!pet) return ''
          return pet.customerName
        }
      }
    ]
  },

  methods: {
    async onGridReady(params: any) {
      this.gridApi = params.api

      if (this.selectedValue == 'allItems') {
        await this.updateRowData()
      } else {
        this.gridApi?.setGridOption('datasource', this.getDatasource())
      }
    },

    getDatasource() {
      const dataSource = {
        rowCount: undefined,
        getRows: async (params: IGetRowsParams) => {
          this.gridApi?.showLoadingOverlay()

          const url = Pet.ENDPOINT
          const urlParams = new URLSearchParams()
          urlParams.set('startRow', String(params.startRow))
          urlParams.set('endRow', String(params.endRow))

          if (params.sortModel.length) {
            urlParams.set('colId', params.sortModel[0].colId)
            urlParams.set('sort', params.sortModel[0].sort)
          }

          if (Object.keys(params.filterModel).length) {
            urlParams.set('query', params.filterModel['-'].filter)
          }

          if (this.searchTerm) {
            urlParams.set('query', this.searchTerm)
          }

          try {
            const res = await this.$http.get<PetsPaginated>(url + `?${urlParams.toString()}`)
            this.rowData = res.data.map((pet) => Pet.from(pet))
            this.gridApi?.hideOverlay()

            const lastRow = res.total
            if (this.rowData.length == 0) {
              this.totalResultCount = 0
            } else {
              this.totalResultCount = res.total
            }
            params.successCallback(this.rowData, lastRow)
          } catch (error: any) {
            this.gridApi?.hideOverlay()
            params.failCallback()
            this.$alertModal.showDanger({ title: error.title, body: error.body })
          }
        }
      }
      return dataSource
    },

    async updateRowData() {
      this.gridApi?.showLoadingOverlay()

      try {
        const response = await this.$http.get<any>(Pet.ENDPOINT)

        // Handle both paginated and non-paginated responses
        const data = response.data || response
        this.rowData = data.map((pet: any) => Pet.from(pet)).filter((pet: Pet) => pet.deletedAt == null)

        this.gridApi?.setGridOption('rowData', this.rowData)
      } catch (error: any) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }

      this.gridApi?.hideOverlay()
    },

    onCellDoubleClicked(event: any) {
      const pet = event.data as Pet
      if (pet && pet.id) {
        this.$router.push(`/pets/pet/${pet.id}/view`)
      }
    },

    filterItems() {
      if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    },


    async delete(items: Pet[]) {
      const confirmed = confirm(this.$t('views.common.listView.selectedRows.deleteConfirm'))
      if (!confirmed) return

      try {
        for (const pet of items) {
          await this.$http.delete(`${Pet.ENDPOINT}/${pet.id}`)
        }
        this.$alertModal.showSuccess({
          title: this.$t('views.common.listView.selectedRows.deleteSuccess')
        })
      } catch (error: any) {
        this.$alertModal.showDanger({ title: error.title, body: error.body })
      }
    },

    async deleteSelectedRows() {
      const selectedRows = this.gridApi?.getSelectedRows()
      if (!selectedRows || selectedRows.length === 0) return

      await this.delete(selectedRows)
      if (this.selectedValue == 'quickView') {
        this.gridApi?.refreshInfiniteCache()
      } else {
        await this.updateRowData()
      }
    },

    filterResult() {
      if (this.rowModelType == 'infinite') {
        this.gridApi?.setFilterModel({'-': {filter: this.searchTerm}})
        return
      }
      this.gridApi?.setQuickFilter(this.searchTerm)
    }
  },

  watch: {
    searchTerm() {
      this.filterResult()
    },

    async selectedValue(_newValue: string, oldValue: string) {
      if (oldValue == 'delete' || oldValue == 'export') {
        return
      }

      if (
        this.selectedValue === 'delete' ||
        this.selectedValue === 'export'
      ) {
        const selected = this.gridApi?.getSelectedRows()

        if (selected?.length) {
          if (this.selectedValue === 'delete') {
            await this.delete(selected)
            if (oldValue == 'quickView') {
              this.gridApi?.refreshInfiniteCache()
            } else {
              await this.updateRowData()
            }
          } else {
            this.gridApi?.exportDataAsCsv({ onlySelected: true })
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }

          // deselect
          selected.length = 0
        }
        this.selectedValue = oldValue
      } else if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    }
  }
})
</script>

<style scoped>
</style>
